import React from "react";
import { Icon } from "@hxnova/icons";

import makeToProgress4dReportEvent from "../../../actions/nav_bar/make_to_progress_4d_report";
import PageNames from "../../../models/domain/enums/page_names";
import { defaultPageOptions, PageOption } from "../nav_bar_pages_dropdown";

// The reskinned top nav regroups the existing nav destinations into three buckets — labeled
// tabs, right-side icon buttons, and a "..." overflow — per the Figma design.
//
// To guarantee permissions carry over unchanged, we REUSE the existing `defaultPageOptions`
// entries (their `visible` predicate and `pageNames`) and override presentation (label text +
// Nova icon) — plus, for the Reports tab only, its `routingEvent` (it points at /reports rather
// than /summary; see reportsPageOption below). Entries are looked up by their stable `displayName`
// literals so permission logic stays single-sourced and the legacy dropdown config is never mutated.

const byName = (displayName: string): PageOption => {
  const option = defaultPageOptions.find((o) => o.displayName === displayName);
  if (!option) {
    throw new Error(`nav_bar_redesign_options: no page option named "${displayName}"`);
  }
  return option;
};

export type RedesignNavItem = {
  key: string
  base: PageOption
  label: string
  // Returns the glyph for the item's current state. The Figma top-nav variants draw each item
  // differently when selected vs not (e.g. outline -> filled), so the icon depends on `active`.
  renderIcon: (active: boolean) => React.ReactNode
  // Width of the label's text box in the Figma frames. Figma rounds text boxes up to whole pixels
  // while the browser uses the exact text width, so without it each tab sits up to 1px off the
  // Figma position. Used as a min-width, so a label whose text renders wider still grows to fit.
  labelMinWidth?: number
};

// Nova material symbol at the library default weight (300), matching the Figma glyphs.
const materialIcon = (name: string, filled: boolean) => <Icon name={name} family="material" filled={filled} size={24}/>;

// The reskinned "Reports" tab reuses the Summary option's permissions and active-state
// (its `visible` predicate + pageNames) but points at the /reports URL instead of /summary.
// makeToProgress4dReportEvent with PROJECT_SUMMARY renders the summary page there directly,
// without the reports-shim redirect. defaultPageOptions is never mutated — this is a derived copy.
const reportsPageOption: PageOption = {
  ...byName("Summary"),
  routingEvent: (projectId: string) => makeToProgress4dReportEvent(projectId, null, false, [PageNames.PROJECT_SUMMARY]),
};

// Center tabs, in Figma order: Dashboard, Reports, Viewer, Files. Per the Figma "Selected=<page>"
// variants every tab is outline when not selected and filled when selected. Colour comes from the
// destination link's CSS.
export const destinationItems: RedesignNavItem[] = [
  { key: "dashboard", base: byName("Dashboard"), label: "Dashboard", labelMinWidth: 72, renderIcon: (active) => materialIcon("dashboard", active) },
  { key: "reports", base: reportsPageOption, label: "Reports", labelMinWidth: 53, renderIcon: (active) => materialIcon("insert_chart", active) },
  { key: "viewer", base: byName("Explorer"), label: "Viewer", labelMinWidth: 44, renderIcon: (active) => materialIcon("deployed_code", active) },
  { key: "files", base: byName("Files"), label: "Files", labelMinWidth: 31, renderIcon: (active) => materialIcon("folder", active) },
];

// Right-side icon buttons: Integrations, Operations. Per the Figma "Selected=Integrations" /
// "Selected=Operations" variants the glyph is outline when not selected and filled when selected;
// the selected colour comes from nav_bar_redesign.css and the selection line from
// NavBarRedesignSelectionLine.
export const iconItems: RedesignNavItem[] = [
  { key: "integrations", base: byName("Integrations"), label: "Integrations", renderIcon: (active) => materialIcon("integration_instructions", active) },
  { key: "operations", base: byName("Operations"), label: "Operations", renderIcon: (active) => materialIcon("build", active) },
];

// "..." overflow menu, per the Figma menu drawn open under "More": Teams then Settings. Per the
// Figma "Type=Underline" Teams frame a row's icon is outline when not selected and filled when
// selected; the selected row's colours come from nav_bar_redesign.css. Budget and
// Schedules are not drawn in Figma; they keep their `visible` predicate and follow in the same
// style, so nobody loses a page they can reach today (Schedules stays a disabled placeholder).
export const overflowItems: RedesignNavItem[] = [
  { key: "teams", base: byName("Team"), label: "Teams", renderIcon: (active) => materialIcon("groups", active) },
  { key: "settings", base: byName("Settings"), label: "Settings", renderIcon: (active) => materialIcon("settings", active) },
  { key: "budget", base: byName("Budget"), label: "Budget", renderIcon: (active) => materialIcon("content_paste", active) },
  { key: "schedules", base: byName("Schedules"), label: "Schedules", renderIcon: (active) => materialIcon("date_range", active) },
];
